// Shared Social Post compose + slot-fill pipeline.
//
// The wizard (StepSocialFinal) does this inline; the Recreate runtime
// needs the same pipeline but without the wizard's UX. This module
// exposes the pure data operations — fill copy slots, compose product
// onto bg via /api/generate, run scene-merge — so any surface can run
// the full Social Post path. Render-to-PNG lives in
// components/SocialPostExporter.tsx because it needs a mounted DOM.
//
// Why a separate file (vs. importing from StepSocialFinal): that
// component is 1000+ lines of wizard-specific state, refs, and
// shimmer UX. Calling into it from the Recreate page would force us
// to mount it and adapt its props, which couples surfaces that don't
// belong together.
//
// MIGRATION NOTE: StepSocialFinal still has its own inline copy of
// this logic. Once the Recreate path proves out in prod, migrate
// StepSocialFinal to call these helpers too (deduplicates the
// compose-cache logic, the scene-merge branch, and the slot-fill
// suggest-copy plumbing).

import {
  composeScene,
  generateImage,
  suggestSocialCopy,
  type SocialPostSlots,
} from '@/lib/api';
import type { SocialDesignId } from '@/pages/app/components/StepSocialDesign';
import {
  SOCIAL_DESIGN_BG_COLOR,
  SOCIAL_DESIGN_SLOT_ASPECT,
  SOCIAL_DESIGN_POST_BG_COLOR,
} from '@/pages/app/components/social-designs/colors';
import { getJsonTemplate } from '@/pages/app/components/social-designs/json-templates/registry';

// All Social Post designs are authored at 1080×1080. Export PNG
// dimensions come from the platform picked on Step 2 (or the recreate
// template's platformId); pixelRatio = exportWidth / DESIGN_REFERENCE_SIZE
// scales the captured pixels to the target.
export const DESIGN_REFERENCE_SIZE = 1080;

// Default values for every slot — used so a design always has
// SOMETHING to render even before the AI fill resolves, and as the
// merge floor under partial AI fills. Lifted from StepSocialFinal's
// PLACEHOLDER_SLOTS; keep the two in sync until StepSocialFinal is
// migrated to import from here.
export const PLACEHOLDER_SLOTS: Required<SocialPostSlots> = {
  eyebrow:       'NEW ARRIVAL',
  headline:      'Crafting your <em>copy…</em>',
  subhead:       'AI is generating headline, subhead, bullets, and CTA based on your product.',
  bullet1:       'Generating proof point…',
  bullet2:       'Generating proof point…',
  bullet3:       'Generating proof point…',
  cta:           'Shop now',
  handle:        '@yourbrand',
  badge_stars:   '★★★★★',
  badge_text:    '4.9 · 12,000 reviews',
  product_name:  'Product name',
  brand_name:    'Brand',
  brand_tagline: 'tagline',
  issue_label:   'Detail',
  caption_left:  'Caption',
  caption_right: 'Label',
  body:          'Generating body copy…',
  bullet_list:   'Generating benefit one\nGenerating benefit two\nGenerating benefit three',
  label_1:       '',
  label_2:       '',
  label_3:       '',
  label_4:       '',
  label_5:       '',
};

/** Merge AI-filled / user-edited slots over the placeholder floor so
 *  every Required slot has a value, even if the AI only filled some
 *  fields. Optional productName overrides the AI fill (passed from the
 *  Details step / Recreate analyze-product result). */
export function buildEffectiveSlots(
  slots: SocialPostSlots | null | undefined,
  productName?: string,
): Required<SocialPostSlots> {
  return {
    ...PLACEHOLDER_SLOTS,
    ...(slots ?? {}),
    product_name:
      slots?.product_name?.trim() ||
      productName?.trim() ||
      PLACEHOLDER_SLOTS.product_name,
  };
}

/** Background color used as the html-to-image capture bg. Falls back
 *  through: legacy design map → JSON design canvas color → cream. */
export function getDesignBackgroundColor(designId: SocialDesignId | string): string {
  return (
    SOCIAL_DESIGN_POST_BG_COLOR[designId] ??
    getJsonTemplate(designId)?.canvas.backgroundColor ??
    '#efe7d8'
  );
}

/** Decode a data URL into a File (sync). Used to feed cropped/composed
 *  product images (which arrive as data URLs) into APIs that take a
 *  File. Pulled out of StepSocialFinal where it was inline. */
export function dataUrlToFile(dataUrl: string, name: string): File {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, comma);
  const b64 = dataUrl.slice(comma + 1);
  const mime = meta.match(/data:(.*?);/)?.[1] ?? 'image/png';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

// ---------------------------------------------------------------------------
// Slot fill
// ---------------------------------------------------------------------------

export interface FillSlotsInput {
  productFile: File;
  productName: string;
  productDescription: string;
}

/** Call /api/suggest-copy with format='social-post-12-slot' and unwrap
 *  the slots. Returns a flat SocialPostSlots that the caller can
 *  shallow-merge with PLACEHOLDER_SLOTS via buildEffectiveSlots. */
export async function fillSocialPostSlots({
  productFile,
  productName,
  productDescription,
}: FillSlotsInput): Promise<SocialPostSlots> {
  const result = await suggestSocialCopy({
    productFile,
    productName,
    productDescription,
  });
  return result.slots;
}

// ---------------------------------------------------------------------------
// Compose (paste OR scene-merge)
// ---------------------------------------------------------------------------

export interface ComposeInput {
  productFile: File;
  designId: SocialDesignId | string;
  productName: string;
  productDescription: string;
  /** From /api/analyze-product. Triggers the SOCIAL_BG_PROMPT_HUMAN
   *  no-shadow prompt variant on the backend (avoids amputation halos
   *  on cropped human models). Optional. */
  hasHumanModel?: boolean;
  /** Optional cropped product image (data URL) from the wizard's
   *  Confirm Cutout step. When present, used as the compose source
   *  in place of the raw upload. The Recreate runtime doesn't have a
   *  crop step so this stays undefined there. */
  croppedProductDataUrl?: string | null;
  /** Optional cost reporter — both compose paths return a per-call
   *  cost via the API client. Surfaces accumulate it for the UI cost
   *  pill. Optional; no-op when omitted. */
  onCost?: (cost: number) => void;
  /** Optional refresh hook fired after every billed call so the
   *  credits balance UI updates immediately. */
  onCreditsChanged?: () => void;
}

export interface ComposeResult {
  /** 'paste' → composedProductUrl is the transparent product PNG to
   *   slot into the design's product-slot.
   *  'scene-merge' → mergedSceneUrl is the AI-merged full scene to
   *   use AS the design's background (the product-slot stays empty;
   *   text overlays still render). */
  mode: 'paste' | 'scene-merge';
  composedProductUrl: string | null;
  mergedSceneUrl: string | null;
}

/** Run the right compose pipeline for the design. JSON templates with
 *  composeMode='scene-merge' go through /api/compose-scene; everything
 *  else goes through /api/generate with `social-bg-<designId>` and
 *  returns a transparent product (rembg-stripped server-side). */
export async function composeSocialPost(input: ComposeInput): Promise<ComposeResult> {
  const {
    productFile,
    designId,
    productName,
    productDescription,
    hasHumanModel,
    croppedProductDataUrl,
    onCost,
    onCreditsChanged,
  } = input;

  const sceneTpl = getJsonTemplate(designId);
  const composeFile = croppedProductDataUrl
    ? dataUrlToFile(croppedProductDataUrl, 'product.png')
    : productFile;

  // ---- SCENE-MERGE path ----
  if (sceneTpl?.composeMode === 'scene-merge') {
    const sceneEl = sceneTpl.elements.find(
      (e) => e.type === 'image' && e.id.endsWith('-background'),
    ) as { src: string } | undefined;
    const slotEl = sceneTpl.elements.find((e) => e.type === 'product-slot') as
      | { x: number; y: number; width: number; height: number }
      | undefined;
    if (sceneEl) {
      const sceneBlob = await (await fetch(sceneEl.src)).blob();
      const slot = slotEl
        ? { x: slotEl.x, y: slotEl.y, w: slotEl.width, h: slotEl.height }
        : undefined;
      const result = await composeScene({
        productFile: composeFile,
        sceneBlob,
        productName,
        productDescription,
        quality: 'low',
        slot,
        canvasSize: sceneTpl.canvas.width,
      });
      onCost?.(result.cost);
      onCreditsChanged?.();
      return { mode: 'scene-merge', composedProductUrl: null, mergedSceneUrl: result.imageUrl };
    }
    // Fall through to paste path if the scene-merge template lacks the
    // expected background element — paste will at least produce SOMETHING.
  }

  // ---- PASTE path ----
  // slotColor is the flat backdrop OpenAI composes the product onto
  // (then rembg strips). JSON templates expose it directly; legacy
  // templates use the SOCIAL_DESIGN_* map.
  const bgHex = sceneTpl
    ? sceneTpl.slotColor
    : SOCIAL_DESIGN_BG_COLOR[designId];
  const aspectRatio = sceneTpl ? '1:1' : SOCIAL_DESIGN_SLOT_ASPECT[designId];
  const result = await generateImage({
    productFile: composeFile,
    templateId: `social-bg-${designId}`,
    frameNumber: 1,
    aspectRatio,
    productName,
    productDescription,
    quality: 'low',
    bgHex,
    hasHumanModel: hasHumanModel === true,
  });
  onCost?.(result.cost);
  onCreditsChanged?.();
  return { mode: 'paste', composedProductUrl: result.imageUrl, mergedSceneUrl: null };
}
